import { XIcon } from "lucide-react";
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { cn } from "@/shared/lib/utils";
import {
  Sheet,
  SheetClose,
  SheetContent,
} from "@/shared/ui/sheet";

import { sheetManager } from "./sheet-manager";
import type { SheetInstance } from "./sheet-types";

function SheetRenderer({
  active,
  sheet,
}: {
  active: boolean;
  sheet: SheetInstance;
}) {
  const CustomComponent = sheet.component;
  const close = useCallback(() => {
    sheetManager.close(sheet.id);
  }, [sheet.id]);

  return (
    <Sheet
      open={active}
      onOpenChange={(open) => {
        if (!open && active) {
          sheetManager.close(sheet.id);
        }
      }}
    >
      <SheetContent
        forceMount
        side={sheet.side}
        showCloseButton={false}
        className={cn(
          "gap-0 outline-none focus:outline-none focus-visible:outline-none data-[state=closed]:duration-200 data-[state=closed]:ease-in data-[state=open]:duration-300 data-[state=open]:ease-out",
          sheet.className,
        )}
        style={sheet.style}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (!active || !sheet.closeOnEsc) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (!active || !sheet.closeOnOutside) {
            event.preventDefault();
          }
        }}
      >
        <CustomComponent
          {...sheet.props}
          close={close}
          resolve={(value: unknown) => sheetManager.resolve(sheet.id, value)}
          reject={(reason?: unknown) => sheetManager.reject(sheet.id, reason)}
        />
        <SheetClose className="absolute top-2 right-2 inline-flex size-7 shrink-0 items-center justify-center rounded-[min(var(--radius-md),12px)] border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap opacity-70 outline-none transition-all select-none hover:bg-muted hover:text-foreground hover:opacity-100 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-expanded:bg-muted aria-expanded:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground dark:hover:bg-muted/50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
          <XIcon />
          <span className="sr-only">关闭</span>
        </SheetClose>
      </SheetContent>
    </Sheet>
  );
}

export function SheetProvider({ children }: React.PropsWithChildren) {
  const sheets = useSyncExternalStore(
    sheetManager.subscribe,
    sheetManager.getState,
    sheetManager.getState,
  );

  useEffect(() => () => sheetManager.closeAll(), []);

  return (
    <>
      {children}
      {sheets.map((sheet, index) => (
        <SheetRenderer
          active={index === sheets.length - 1 && sheet.status === "open"}
          key={sheet.id}
          sheet={sheet}
        />
      ))}
    </>
  );
}
